/**
 * ArchiveScoutReportDialog Container Component
 *
 * Renders the shared ConfirmDialog molecule (SE-5524) with the
 * type-to-confirm "archive" gate. The molecule owns the typed-input state
 * (resetting it when the dialog closes); this container owns only the
 * close-during-archival guard. The former bespoke View's hex-waiver theme
 * styling is intentionally standardized onto closed tokens.
 *
 * On confirm the container hands the parent the literal backend
 * confirmation token — the GraphQL mutation requires exactly "archive",
 * and the molecule's gate guarantees the user typed it (case-insensitively)
 * before confirm can fire. The actual archive mutation is owned by the
 * parent component.
 * @module features/reports/components/ArchiveScoutReportDialog
 */

import { useCallback } from "react";
import { Platform } from "react-native";

import ConfirmDialog from "@/components/molecules/ConfirmDialog";

/** Literal confirmation token the archiveScoutReport mutation requires. */
const ARCHIVE_CONFIRMATION_TOKEN = "archive";

/** Props for ArchiveScoutReportDialogContainer */
export interface ArchiveScoutReportDialogContainerProps {
  /** Whether the dialog is visible */
  readonly isOpen: boolean;
  /** Whether archival is in progress */
  readonly isArchiving: boolean;
  /** Callback invoked when the dialog is dismissed */
  readonly onClose: () => void;
  /** Callback invoked when user confirms archival with the confirmation text */
  readonly onConfirm: (confirmation: string) => void;
}

/**
 * Container component for the archive scout report confirmation dialog.
 * @param root0 - Component props
 * @param root0.isOpen - Whether the dialog is visible
 * @param root0.isArchiving - Whether archival is in progress
 * @param root0.onClose - Callback invoked when the dialog is dismissed
 * @param root0.onConfirm - Callback invoked when user confirms archival
 */
const ArchiveScoutReportDialogContainer = ({
  isOpen,
  isArchiving,
  onClose,
  onConfirm,
}: ArchiveScoutReportDialogContainerProps) => {
  const handleClose = useCallback(() => {
    if (!isArchiving) {
      onClose();
    }
  }, [isArchiving, onClose]);

  const handleConfirm = useCallback(() => {
    onConfirm(ARCHIVE_CONFIRMATION_TOKEN);
  }, [onConfirm]);

  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title="Archive Report"
      body={[
        "Are you sure you want to archive this report?",
        "To confirm, please type “archive” below.",
        "Note: Archived reports will be retained for up to 6 months and will be automatically deleted afterwards.",
      ]}
      tone="danger"
      confirmLabel={isArchiving ? "Archiving..." : "Archive Report"}
      isConfirming={isArchiving}
      requireTypedConfirmation={{
        expectedText: ARCHIVE_CONFIRMATION_TOKEN,
        placeholder: "Type 'archive' to confirm",
      }}
      // Capability gating (not layout): native RN Modal stacking so the
      // dialog renders above the parent report modal on iOS/Android.
      useRNModal={Platform.OS !== "web"}
      testID="archive-report-dialog"
      cancelTestID="archive-report-cancel"
      confirmTestID="archive-report-confirm"
      inputTestID="archive-confirmation-input"
    />
  );
};

ArchiveScoutReportDialogContainer.displayName =
  "ArchiveScoutReportDialogContainer";

export default ArchiveScoutReportDialogContainer;
